export interface IUsHoliday {
  date: string; // ISO yyyy-mm-dd
  name: string; // short label, e.g. "Thanksgiving"
}

const pad = (n: number) => String(n).padStart(2, '0');
const iso = (y: number, m: number, d: number) => `${y}-${pad(m)}-${pad(d)}`;

/** nth weekday (0=Sun..6=Sat) of a given month/year. nth=-1 -> last. */
const nthWeekday = (y: number, m: number, weekday: number, nth: number): string => {
  if (nth > 0) {
    const first = new Date(y, m - 1, 1).getDay();
    const offset = (weekday - first + 7) % 7;
    return iso(y, m, 1 + offset + (nth - 1) * 7);
  }
  // last
  const lastDay = new Date(y, m, 0).getDate();
  const last = new Date(y, m - 1, lastDay).getDay();
  const offset = (last - weekday + 7) % 7;
  return iso(y, m, lastDay - offset);
};

/** Easter Sunday (Western, Anonymous Gregorian / computus algorithm). */
const easter = (y: number): string => {
  const a = y % 19;
  const b = Math.floor(y / 100);
  const c = y % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return iso(y, month, day);
};

/** Build the full list of holidays for a calendar year. */
const yearHolidays = (y: number): IUsHoliday[] => {
  const thanksgiving = nthWeekday(y, 11, 4, 4); // 4th Thu of Nov
  const t = new Date(`${thanksgiving}T00:00:00`);
  const bf = new Date(t);
  bf.setDate(bf.getDate() + 1);
  const cm = new Date(t);
  cm.setDate(cm.getDate() + 4);
  const blackFriday = iso(bf.getFullYear(), bf.getMonth() + 1, bf.getDate());
  const cyberMonday = iso(cm.getFullYear(), cm.getMonth() + 1, cm.getDate());
  return [
    { date: iso(y, 1, 1), name: "New Year's Day" },
    { date: nthWeekday(y, 1, 1, 3), name: 'MLK Day' },
    { date: iso(y, 2, 14), name: "Valentine's Day" },
    { date: nthWeekday(y, 2, 1, 3), name: "Presidents' Day" },
    { date: easter(y), name: 'Easter' },
    { date: nthWeekday(y, 5, 0, 2), name: "Mother's Day" },
    { date: nthWeekday(y, 5, 1, -1), name: 'Memorial Day' },
    { date: nthWeekday(y, 6, 0, 3), name: "Father's Day" },
    { date: iso(y, 6, 19), name: 'Juneteenth' },
    { date: iso(y, 7, 4), name: 'Independence Day' },
    { date: nthWeekday(y, 9, 1, 1), name: 'Labor Day' },
    { date: nthWeekday(y, 10, 1, 2), name: 'Columbus Day' },
    { date: iso(y, 10, 31), name: 'Halloween' },
    { date: iso(y, 11, 11), name: 'Veterans Day' },
    { date: thanksgiving, name: 'Thanksgiving' },
    { date: blackFriday, name: 'Black Friday' },
    { date: cyberMonday, name: 'Cyber Monday' },
    { date: iso(y, 12, 25), name: 'Christmas' },
  ];
};

/** Holidays whose date falls inside [startIso, endIso] inclusive (yyyy-mm-dd strings). */
export const getUsHolidaysInRange = (startIso: string, endIso: string): IUsHoliday[] => {
  const startY = Number(startIso.slice(0, 4));
  const endY = Number(endIso.slice(0, 4));
  const all: IUsHoliday[] = [];
  for (let y = startY; y <= endY; y++) all.push(...yearHolidays(y));
  return all.filter(h => h.date >= startIso && h.date <= endIso);
};
